import { keepPreviousData, queryOptions } from "@tanstack/react-query"

import { getIsomorphicSupabaseClient } from "@repo/supabase/isomorphic-client"
import {
  type CurrencyStatus,
  createSystemConfigService,
  type LanguageProficiencyLevelStatus,
  type LocaleStatus,
  type SystemInvoiceItemStatus,
} from "./system-config.service"

export const getAvailableLanguageProficiencyLevelsQueryOptions = (
  status: LanguageProficiencyLevelStatus[] = ["active"],
) =>
  queryOptions({
    queryKey: Array.isArray(status)
      ? ["available-language-proficiency-levels", ...status.sort()]
      : ["available-language-proficiency-levels"],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const systemConfigService = createSystemConfigService(supabase)
      return systemConfigService.getAvailableLanguageProficiencyLevels(status)
    },
    staleTime: 1000 * 60 * 20, // 20 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  })

export const getAvailableLocalesQueryOptions = (
  status: LocaleStatus[] = ["active"],
) =>
  queryOptions({
    queryKey: Array.isArray(status)
      ? ["available-locales", ...status.sort()]
      : ["available-locales"],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const systemConfigService = createSystemConfigService(supabase)
      return systemConfigService.getAvailableLocales(status)
    },
    staleTime: 1000 * 60 * 20, // 20 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  })

export const getAvailableGlobalCurrenciesQueryOptions = (
  status: CurrencyStatus[] = ["active"],
) =>
  queryOptions({
    queryKey: Array.isArray(status)
      ? ["available-currencies", ...status.sort()]
      : ["available-currencies"],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const systemConfigService = createSystemConfigService(supabase)
      return systemConfigService.getAvailableCurrencies(status)
    },
  })

export const getAvailableSystemInvoiceItemsQueryOptions = (
  status: SystemInvoiceItemStatus[] = ["active"],
) =>
  queryOptions({
    queryKey: Array.isArray(status)
      ? ["available-system-invoice-items", ...status.sort()]
      : ["available-system-invoice-items"],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const systemConfigService = createSystemConfigService(supabase)
      return systemConfigService.getAvailableSystemInvoiceItems(status)
    },
  })

export const getCurrencyExchangeRatesQueryOptions = (
  filters: { limit?: number; offset?: number; since?: Date } = {},
) =>
  queryOptions({
    queryKey: ["currency-exchange-rates", filters],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const systemConfigService = createSystemConfigService(supabase)
      return systemConfigService.getCurrencyExchangeRates(filters)
    },
    staleTime: 1000 * 60 * 20, // 20 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
  })
